type TMappWebViewBridge = {
  authenticate?: unknown;
  bytePulseDevMode?: unknown;
  /** SuperApp app locale (e.g. `en-gb`) injected by MicroAppWebView. */
  locale?: string;
  /** SuperApp MicroAppWebView — `window.mapp.navigation.handleDismiss`. */
  navigation?: {
    handleDismiss?: () => void;
  };
  /** Optional host-provided close/back handlers (names vary by SuperApp build). */
  close?: () => void;
  goBack?: () => void;
  dismiss?: () => void;
};

type TReactNativeWebView = { postMessage?: (message: string) => void };

/** True when Pulse runs inside SuperApp / HutBot MicroAppWebView (injected `window.mapp.authenticate`). */
export function isEmbeddedInSuperAppWebView(): boolean {
  const mapp = (window as Window & { mapp?: TMappWebViewBridge }).mapp;

  return typeof mapp?.authenticate === 'function';
}

/** Device/app locale from SuperApp when embedded in MicroAppWebView. */
export function getSuperAppDeviceLocale(): string | undefined {
  const locale = (window as Window & { mapp?: TMappWebViewBridge }).mapp?.locale;

  return typeof locale === 'string' && locale.length > 0 ? locale : undefined;
}

/**
 * Locale query param for GET /forms/:id/render — only when opened inside SuperApp.
 * Standalone web omits `locale` so the API uses the form default.
 */
export function resolveRenderApiLocale(): string | undefined {
  if (!isEmbeddedInSuperAppWebView()) return undefined;

  const deviceLocale = getSuperAppDeviceLocale();
  if (!deviceLocale) return undefined;

  return deviceLocale.split(/[-_]/)[0] || undefined;
}

/** True when SuperApp injected `dev_BytePulseDevMode` via `window.mapp.bytePulseDevMode`. */
export function isBytePulseDevModeEnabled(): boolean {
  const mapp = (window as Window & { mapp?: TMappWebViewBridge }).mapp;

  return mapp?.bytePulseDevMode === true;
}

/**
 * Best-effort "return to the SuperApp" after a successful survey submit
 * (BCEXP-9223). The MicroApp SDK exposes no close method, so we try, in order:
 *   1. `window.mapp.navigation.handleDismiss` (SuperApp MicroAppWebView),
 *   2. a close/back/dismiss function on the injected `window.mapp` bridge,
 *   3. the React Native WebView bridge (`DISMISS` — same as `window.mapp.navigation.handleDismiss`),
 *   4. `window.close()` for browser micro-app popups.
 *
 * Returns true if a close mechanism was invoked; false if none was available
 * (the caller can then fall back to in-app navigation).
 */
export function closeSurveyView(): boolean {
  const win = window as Window & {
    mapp?: TMappWebViewBridge;
    ReactNativeWebView?: TReactNativeWebView;
  };

  const mapp = win.mapp;
  const navigationDismiss = mapp?.navigation?.handleDismiss;
  if (typeof navigationDismiss === 'function') {
    navigationDismiss();
    return true;
  }

  const handler = mapp?.close ?? mapp?.goBack ?? mapp?.dismiss;
  if (typeof handler === 'function') {
    handler();
    return true;
  }

  if (typeof win.ReactNativeWebView?.postMessage === 'function') {
    win.ReactNativeWebView.postMessage(JSON.stringify({ type: 'DISMISS' }));
    return true;
  }

  if (typeof win.close === 'function') {
    win.close();
    return true;
  }

  return false;
}
